import React, { useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { PdfGenerationContext } from "./PdfGenerationContext";
import MobilePdfViewer from "./MobilePdfViewer";

export const MobileBottomBarContext = React.createContext(null);

const styles = {
  dialogPaper: {
    height: "100dvh",
    maxHeight: "100dvh",
    margin: 0,
    maxWidth: "100vw",
    borderRadius: 0,
    "@supports not (height: 100dvh)": {
      height: "100vh",
      maxHeight: "-webkit-fill-available",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 2,
    py: 1,
    borderBottom: 1,
    borderColor: "divider",
    bgcolor: "#1a1a2e",
    color: "white",
  },
  chip: {
    bgcolor: "rgba(255,255,255,0.1)",
    color: "white",
    fontSize: 14,
  },
  regenerateButton: {
    color: "white",
    borderColor: "rgba(255,255,255,0.5)",
    textTransform: "none",
    "&:hover": {
      borderColor: "white",
      bgcolor: "rgba(255,255,255,0.1)",
    },
    "&.Mui-disabled": {
      color: "rgba(255,255,255,0.3)",
      borderColor: "rgba(255,255,255,0.1)",
    },
  },
  pdfContainer: {
    flexGrow: 1,
    bgcolor: "#525659",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
  },
  wizardContainer: (isPdfGenerating) => ({
    width: 480,
    minWidth: 480,
    maxWidth: 480,
    bgcolor: "background.paper",
    borderLeft: 1,
    borderColor: "divider",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    opacity: isPdfGenerating ? 0.7 : 1,
    pointerEvents: isPdfGenerating ? "none" : "auto",
  }),
  mobileBottomBar: {
    display: "flex",
    gap: 2,
    p: 2,
    borderTop: 1,
    borderColor: "divider",
    bgcolor: "background.paper",
    flexShrink: 0,
  },
};

const ReceivingWizardDialog = ({ children, onClose, pdfUrl, shipment, ...props }) => {
  const iframeRef = useRef(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [bottomActions, setBottomActions] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mobileCtxValue = useMemo(() => ({ isMobile, setBottomActions }), [isMobile]);

  const pdfIframeSrc = useMemo(() => {
    if (!pdfUrl) return null;

    // Open PDF with thumbnails sidebar automatically visible
    try {
      const [base, fragment = ""] = pdfUrl.split("#");
      const params = new URLSearchParams(fragment);
      params.set("pagemode", "thumbs"); // Auto-open thumbnails sidebar
      const separator = base.includes("?") ? "&" : "?";
      const timestampedBase = `${base}${separator}t=${refreshKey}`;
      return `${timestampedBase}#${params.toString()}`;
    } catch {
      return pdfUrl;
    }
  }, [pdfUrl, refreshKey]);

  const handleRegenerate = () => {
    if (pdfUrl) {
      setIsPdfGenerating(true);
      setRefreshKey(Date.now());
    }
  };

  const handleIframeLoad = () => {
    try {
      const iframe = iframeRef.current;

      if (iframe && iframe.contentWindow) {
        const iframeContent = iframe.contentWindow.document.body.innerText;
        if (iframeContent && iframeContent.includes("Just a sec! I'm generating your new PDF.")) {
          setIsPdfGenerating(true);
        } else {
          setIsPdfGenerating(false);
        }
      }
    } catch (error) {
      console.log("Iframe loaded (cross-origin restricted)");
      setIsPdfGenerating(false);
    }
  };

  return (
    <Dialog
      {...props}
      maxWidth={false}
      fullWidth
      onClose={onClose}
      PaperProps={{
        sx: styles.dialogPaper,
      }}
    >
      <PdfGenerationContext.Provider value={isPdfGenerating}>
        <MobileBottomBarContext.Provider value={mobileCtxValue}>
          <Box display="flex" flexDirection="column" height="100%">
            {/* Header */}
            <Box sx={styles.header}>
              <Box display="flex" alignItems="center" gap={2}>
                {shipment?.project?.name && (
                  <Chip
                    label={shipment.project.name}
                    size="small"
                    sx={styles.chip}
                  />
                )}
                <Typography variant="subtitle1" fontWeight={400}>
                  {shipment?.shipmentId || "Shipment Details"}
                  {shipment?.completer && (
                    <span style={{ fontWeight: 400 }}>
                      {" - "}
                      {shipment.completer.name || shipment.completer.email}
                    </span>
                  )}
                </Typography>
                {shipment?.draft && (
                  <Chip label="Draft" size="small" color="warning" sx={{ fontSize: 12 }} />
                )}
                {!isMobile && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleRegenerate}
                    disabled={isPdfGenerating}
                    sx={styles.regenerateButton}
                  >
                    Regenerate PDF
                  </Button>
                )}
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                {isMobile && (
                  <IconButton
                    onClick={handleRegenerate}
                    size="small"
                    disabled={isPdfGenerating}
                    sx={{ color: "white" }}
                  >
                    <RefreshIcon />
                  </IconButton>
                )}
                <IconButton onClick={onClose} size="small" sx={{ color: "white" }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* PDF + Wizard layout */}
            <Box display="flex" flexGrow={1} overflow="hidden">
              {/* PDF Viewer — open-in-new-tab prompt on mobile, iframe on desktop */}
              <Box sx={styles.pdfContainer}>
                {isMobile ? (
                  pdfIframeSrc ? (
                    <MobilePdfViewer url={pdfIframeSrc} />
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Typography color="grey.400">No PDF available</Typography>
                    </Box>
                  )
                ) : pdfIframeSrc ? (
                  <iframe
                    ref={iframeRef}
                    src={pdfIframeSrc}
                    onLoad={handleIframeLoad}
                    title="Shipment PDF"
                    style={{
                      border: "none",
                      width: "100%",
                      height: "100%",
                      flexGrow: 1,
                    }}
                  />
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography color="grey.400">No PDF available</Typography>
                  </Box>
                )}
              </Box>

              {/* Right: Receiving Wizard — hidden on mobile but kept mounted so effects fire */}
              <Box sx={[styles.wizardContainer(isPdfGenerating), isMobile && { display: "none" }]}>
                {children}
              </Box>
            </Box>

            {/* Mobile sticky bottom action bar */}
            {isMobile && bottomActions && (
              <Box sx={styles.mobileBottomBar}>
                {bottomActions}
              </Box>
            )}
          </Box>
        </MobileBottomBarContext.Provider>
      </PdfGenerationContext.Provider>
    </Dialog>
  );
};

export default ReceivingWizardDialog;
